import React, { useState } from 'react';
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { ListGroup, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Trash2, Edit2, X, Save, RefreshCw } from 'lucide-react';
import { SyncService } from '../services/SyncService';

const ShoppingList = ({ socket }) => {
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Détection automatique des changements dans la base locale (Dexie)
  const allItems = useLiveQuery(() => db.items.toArray());
  const householdId = localStorage.getItem('household_id');

  // Fonction pour notifier les autres via Socket
  const notifyOthers = () => {
    if (socket && householdId) {
      socket.emit('send_update', householdId);
      console.log("⚡ Signal envoyé : La liste a été modifiée !");
    }
  };

  if (!allItems) return <div className="text-center p-5"><Spinner animation="border" variant="success" /></div>;

  // Tri : les articles terminés (done) vont en bas
  const sortedItems = [...allItems].sort((a, b) => {
    if (a.status === 'done' && b.status !== 'done') return 1;
    if (a.status !== 'done' && b.status === 'done') return -1;
    return 0;
  });

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await SyncService.pullServerChanges();
    setTimeout(() => setIsRefreshing(false), 800);
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditData(item);
  };

  const handleSave = async (id) => {
    const updatedItem = { ...editData, updated_at: new Date().toISOString() };
    await db.items.update(id, updatedItem);
    setEditId(null);
    await SyncService.pushLocalChanges();
    notifyOthers(); // <-- Notification
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === 'done' ? 'pending' : 'done';
    await db.items.update(item.id, { 
      status: newStatus, 
      updated_at: new Date().toISOString() 
    });
    await SyncService.pushLocalChanges();
    notifyOthers(); // <-- Notification
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cet article ?")) {
      await db.items.delete(id);
      await SyncService.pushLocalChanges();
      notifyOthers(); // <-- Notification
    }
  };

  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold mb-0">Ma Liste</h5>
        <Button 
          variant="light" 
          size="sm" 
          onClick={handleManualRefresh} 
          disabled={isRefreshing}
          className="rounded-circle shadow-sm"
        >
          <RefreshCw size={18} className={isRefreshing ? "spin-animation" : ""} />
        </Button>
      </div>

      <ListGroup className="shadow-sm rounded-4 overflow-hidden border-0">
        {sortedItems.map(item => (
          <ListGroup.Item key={item.id} className="border-bottom border-light p-3">
            {editId === item.id ? (
              <Row className="g-2">
                <Col xs={6}><Form.Control size="sm" value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} /></Col>
                <Col xs={6}><Form.Control size="sm" value={editData.quantity} onChange={e => setEditData({...editData, quantity: e.target.value})} /></Col>
                <Col xs={6} className="d-flex gap-2">
                  <Button variant="success" size="sm" onClick={() => handleSave(item.id)}><Save size={16}/></Button>
                  <Button variant="light" size="sm" onClick={() => setEditId(null)}><X size={16}/></Button>
                </Col>
              </Row>
            ) : (
              <div className="d-flex justify-content-between align-items-center">
                <div onClick={() => toggleStatus(item)} style={{ cursor: 'pointer', flex: 1 }}>
                  <span className={item.status === 'done' ? 'text-decoration-line-through text-muted' : 'fw-bold'}>
                    {item.name}
                  </span>
                  <div className="small text-muted">{item.quantity} • {item.category}</div>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="link" className="text-muted p-1" onClick={() => startEdit(item)}>
                    <Edit2 size={18}/>
                  </Button>
                  <Button variant="link" className="text-danger p-1" onClick={() => handleDelete(item.id)}>
                    <Trash2 size={18}/>
                  </Button>
                </div>
              </div>
            )}
          </ListGroup.Item>
        ))}
      </ListGroup>

      <style>{`
        .spin-animation { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .text-decoration-line-through { text-decoration: line-through; }
      `}</style>
    </div>
  );
};

export default ShoppingList;